import React from 'react';
import { IMaskInput } from 'react-imask';
import styled from 'styled-components';
import { masks } from '@src/common/masks';
import { LayoutColors } from '@src/core/theme';
import { BodyExtraSmall, BodySmall } from '@src/fonts/typography/Typography';

type Mask = keyof typeof masks;

type InputProps = Omit<JSX.IntrinsicElements['input'], 'name'> & {
  name: string;
  mask?: Mask | Mask[];
  showErrorLabel?: boolean;
  error?: string | null;
  label?: string;
  hint?: React.ReactNode | string;
};

const Input = ({ mask, error, showErrorLabel = true, label, hint, ...props }: InputProps) => {
  const getMask = (mask: string | string[]) => {
    if (Array.isArray(mask)) {
      return mask.map((mask) => ({ mask: masks[mask as keyof typeof masks] }));
    }

    return [{ mask: masks[mask as keyof typeof masks] }];
  };

  return (
    <WrapperInputMask>
      {label ? <Label htmlFor={props.name}>{label}</Label> : null}
      <StyledInput {...props} $hasError={Boolean(error)} mask={mask ? getMask(mask) : undefined} id={props.name} />
      {showErrorLabel ? <ErrorLabel>{error}</ErrorLabel> : null}
      {hint ?? null}
    </WrapperInputMask>
  );
};

interface StyledInputProps {
  $hasError: boolean;
}

const WrapperInputMask = styled.div`
  width: 100%;
  ${BodySmall};
`;

const Label = styled.label`
  ${BodySmall};
  margin-bottom: 8px;
`;

const ErrorLabel = styled.p`
  ${BodyExtraSmall};
  color: ${LayoutColors.errorDark};
`;

const StyledInput = styled(IMaskInput)<StyledInputProps>`
  width: 100%;
  margin-bottom: 4px;
  border-radius: 4px;
  background-color: ${LayoutColors.whiteLight};
  border: 1px solid ${LayoutColors.blackLight};
  padding: 12px;
  height: 46px;
  ${(props) => (props.$hasError ? `border-color: ${LayoutColors.errorDark}` : '')};
`;

export default Input;
